import { useEffect, useRef, useState } from "react";

const TRANSITION_DURATION_MS = 260;

export const AnimatedStatusText = ({
    text,
    className = "",
}: {
    text: string;
    className?: string;
}) => {
    const previousTextRef = useRef(text);
    const timeoutRef = useRef<number | null>(null);
    const [transition, setTransition] = useState<{
        current: string;
        previous: string | null;
    }>({
        current: text,
        previous: null,
    });

    useEffect(() => {
        if (text === previousTextRef.current) return;

        const previous = previousTextRef.current;
        previousTextRef.current = text;
        if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);

        // Token-by-token growth should feel like natural streaming, not trigger
        // a full crossfade for every appended character.
        if (text.startsWith(previous)) {
            setTransition({ current: text, previous: null });
            return;
        }

        setTransition({ current: text, previous });
        timeoutRef.current = window.setTimeout(() => {
            setTransition(current => (
                current.current === text
                    ? { ...current, previous: null }
                    : current
            ));
            timeoutRef.current = null;
        }, TRANSITION_DURATION_MS);
    }, [text]);

    useEffect(() => () => {
        if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    }, []);

    return (
        <span
            className={`animated-status-text ${className}`.trim()}
            aria-live="polite"
        >
            {transition.previous && (
                <span
                    className="animated-status-value is-leaving"
                    aria-hidden="true"
                >
                    {transition.previous}
                </span>
            )}
            <span
                key={transition.current}
                className={`animated-status-value${transition.previous ? " is-entering" : ""}`}
            >
                {transition.current}
            </span>
        </span>
    );
};
